import { Recipe } from '@/types/recipe';
import { generateImageDirectlyForRecipe } from '@/services/geminiService';

/**
 * Module-level singleton for the studio background image job.
 *
 * The job is started by Step4Review right after recipe generation and was
 * previously fire-and-forget: if the user finalized before it completed, the
 * image was silently discarded (autoGenerateImage:false blocked regeneration).
 * Keeping the promise here lets the review page await it at finalize time.
 */

type ImageJobResult = Awaited<ReturnType<typeof generateImageDirectlyForRecipe>>;

let activeJob: Promise<ImageJobResult> | null = null;

/** Starts (or replaces) the background image job for a studio recipe. */
export function startStudioImageJob(recipe: Recipe): Promise<ImageJobResult> {
  activeJob = generateImageDirectlyForRecipe(recipe).finally(() => {
    // Keep the settled result cached briefly? No — consumers await the same
    // promise instance, so it stays awaitable after settlement. We only clear
    // explicitly via clearStudioImageJob() (called after finalize/save).
  });
  return activeJob;
}

/** Returns the in-flight (or settled) image job, or null if none was started. */
export function getStudioImageJob(): Promise<ImageJobResult> | null {
  return activeJob;
}

/** Clears the cached job (call after it has been consumed or is no longer relevant). */
export function clearStudioImageJob(): void {
  activeJob = null;
}
